import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { authenticatedApiClient as api } from '../../api/api';
import { PaginationMeta } from '../../types/pagination';
import { ApiRole, ApiPermission, PermissionsGroupedByCategory, RoleQueryParams, PermissionQueryParams } from './types/api-types';
import { 
  getAllRolesAPI, 
  getAllPermissionsAPI, 
  getPermissionsGroupedByCategoryAPI,
  deleteRoleAPI 
} from './api/rolePermissionAPI';

export interface User {
  _id: string;
  uuid: string;
  email: string;
  first_name: string;
  last_name: string;
  phone?: string;
  profilePicture?: string;
  role: 'admin' | 'manager' | 'cashier' | 'staff' | 'user';
  storeIds?: string[]; // Store IDs - binds user to specific stores (array)
  roleIds?: string[]; // NEW: Array of role UUIDs
  additionalPermissionIds?: string[]; // NEW: Additional custom permissions
  permissions?: string[];
  effectivePermissions?: Array<{ // NEW: Computed effective permissions from backend
    uuid: string;
    action: string;
    name: string;
    category: string;
  }>;
  isActive: boolean;
  isDeleted?: boolean;
  deletedAt?: string;
  deletedBy?: string;
  createdAt: string;
  updatedAt: string;
}

export interface UserQueryParams {
  search?: string;
  role?: string;
  page?: number;
  limit?: number;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  includeDeleted?: boolean;
}

export interface CreateUserData {
  email: string;
  first_name: string;
  last_name: string;
  phone?: string;
  password: string;
  role: string;
  roleIds?: string[]; // NEW: Array of role UUIDs
  permissions?: string[]; // Legacy permissions for backward compatibility
  additionalPermissionIds?: string[]; // NEW: Additional custom permissions
  isActive?: boolean;
}

export interface UpdateUserData {
  email?: string;
  first_name?: string;
  last_name?: string;
  phone?: string;
  profilePicture?: string;
  role?: string;
  roleIds?: string[]; // NEW: Array of role UUIDs
  permissions?: string[]; // Legacy permissions for backward compatibility  
  additionalPermissionIds?: string[]; // NEW: Additional custom permissions
  isActive?: boolean;
}

export interface UsersResponse {
  data: User[];
  pagination: PaginationMeta;
}

export interface RolesState {
  search?: string;
  includeDeleted?: boolean;
  includeSystemRoles?: boolean;
  page?: number;
  limit?: number;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}

export interface AdminState {
  users: User[];
  currentUser: User | null;
  pagination: PaginationMeta;
  filters: UserQueryParams;
  loading: boolean;
  error: string | null;
  statistics: {
    totalUsers: number;
    activeUsers: number;
    inactiveUsers: number;
    roleStats: Array<{
      role: string;
      count: number;
      active: number;
      inactive: number;
    }>;
  } | null;
  // NEW: Role and Permission Management
  roles: ApiRole[];
  rolesPagination: PaginationMeta;
  rolesFilters: RolesState;
  permissions: ApiPermission[];
  permissionsGrouped: PermissionsGroupedByCategory;
  rolesLoading: boolean;
  permissionsLoading: boolean;
  rolePermissionError: string | null;
}

const initialState: AdminState = {
  users: [],
  currentUser: null,
  pagination: {
    currentPage: 1,
    totalPages: 0,
    totalItems: 0,
    pageSize: 10,
    hasNext: false,
    hasPrev: false,
  },
  filters: {
    page: 1,
    limit: 10,
  },
  loading: false,
  error: null,
  statistics: null,
  // NEW: Role and Permission Management
  roles: [],
  rolesPagination: {
    currentPage: 1,
    totalPages: 0,
    totalItems: 0,
    pageSize: 10,
    hasNext: false,
    hasPrev: false,
  },
  rolesFilters: {
    page: 1,
    limit: 10,
    includeDeleted: false,
    includeSystemRoles: true,
  },
  permissions: [],
  permissionsGrouped: {},
  rolesLoading: false,
  permissionsLoading: false,
  rolePermissionError: null,
};

// Async Thunks
export const fetchUsers = createAsyncThunk(
  'admin/fetchUsers',
  async (params: UserQueryParams = {}) => {
    const queryParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        queryParams.append(key, value.toString());
      }
    });

    const response = await api.get(`/admin/users?${queryParams.toString()}`);
    // Handle ResponseInterceptor format: { status, message, data, ... }
    if (response.data.status && response.data.data) {
      // response.data.data contains { data: [...], pagination: {...} }
      return response.data.data;
    }
    throw new Error(response.data.message || 'Failed to fetch users');
  }
);

export const fetchUserById = createAsyncThunk(
  'admin/fetchUserById',
  async (uuid: string, { rejectWithValue }) => {
    try {
      const response = await api.get(`/admin/users/${uuid}`);
      // Handle standardized response format
      if (response.data.status && response.data.data) {
        return response.data.data;
      }
      throw new Error(response.data.message || 'Failed to fetch user');
    } catch (error: any) {
      if (error.response?.data) {
        return rejectWithValue(error.response.data);
      }
      return rejectWithValue({ message: error.message || 'Network error occurred' });
    }
  }
);

export const createUser = createAsyncThunk(
  'admin/createUser',
  async (userData: CreateUserData, { rejectWithValue }) => {
    try {
      // Sanitize roleIds and additionalPermissionIds before sending
      const sanitizedUserData = {
        ...userData,
        roleIds: Array.isArray(userData.roleIds) 
          ? userData.roleIds.map(id => String(id)).filter(Boolean)
          : [],
        additionalPermissionIds: Array.isArray(userData.additionalPermissionIds)
          ? userData.additionalPermissionIds.map(id => String(id)).filter(Boolean)
          : []
      };
      
      const response = await api.post('/admin/users', sanitizedUserData);
      // Handle standardized response format
      if (response.data.status && response.data.data) {
        return response.data.data;
      }
      throw new Error(response.data.message || 'Failed to create user');
    } catch (error: any) {
      if (error.response?.data) {
        return rejectWithValue(error.response.data);
      }
      return rejectWithValue({ message: error.message || 'Network error occurred' });
    }
  }
);

export const updateUser = createAsyncThunk(
  'admin/updateUser',
  async ({ uuid, userData }: { uuid: string; userData: UpdateUserData }, { rejectWithValue }) => {
    try {
      // Sanitize roleIds and additionalPermissionIds before sending
      const sanitizedUserData = {
        ...userData,
        roleIds: Array.isArray(userData.roleIds) 
          ? userData.roleIds.map(id => String(id)).filter(Boolean)
          : undefined,
        additionalPermissionIds: Array.isArray(userData.additionalPermissionIds)
          ? userData.additionalPermissionIds.map(id => String(id)).filter(Boolean)
          : undefined
      };
      
      const response = await api.put(`/admin/users/${uuid}`, sanitizedUserData);
      // Handle standardized response format
      if (response.data.status && response.data.data) {
        return response.data.data;
      }
      throw new Error(response.data.message || 'Failed to update user');
    } catch (error: any) {
      if (error.response?.data) {
        return rejectWithValue(error.response.data);
      }
      return rejectWithValue({ message: error.message || 'Network error occurred' });
    }
  }
);

export const deleteUser = createAsyncThunk(
  'admin/deleteUser',
  async (uuid: string) => {
    await api.delete(`/admin/users/${uuid}`);
    return uuid;
  }
);

export const restoreUser = createAsyncThunk(
  'admin/restoreUser',
  async (uuid: string) => {
    const response = await api.post(`/admin/users/${uuid}/restore`);
    return { uuid, data: response.data };
  }
);

export const updateUserRole = createAsyncThunk(
  'admin/updateUserRole',
  async ({ uuid, role }: { uuid: string; role: string }) => {
    const response = await api.put(`/admin/users/${uuid}/role`, { role });
    return response.data;
  }
);

export const toggleUserStatus = createAsyncThunk(
  'admin/toggleUserStatus',
  async ({ uuid, isActive }: { uuid: string; isActive: boolean }) => {
    const endpoint = isActive ? 'activate' : 'deactivate';
    const response = await api.post(`/admin/users/${uuid}/${endpoint}`);
    return response.data;
  }
);

export const fetchStatistics = createAsyncThunk(
  'admin/fetchStatistics',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get('/admin/statistics');
      console.log('fetchStatistics response:', response.data);
      
      // Handle standardized response format
      if (response.data.status && response.data.data) {
        return response.data.data;
      }
      
      // Handle direct data response (backward compatibility)
      if (response.data.totalUsers !== undefined) {
        return response.data;
      }
      
      throw new Error(response.data.message || 'Failed to fetch statistics');
    } catch (error: any) {
      console.error('fetchStatistics error:', error);
      if (error.response?.data) {
        return rejectWithValue(error.response.data);
      }
      return rejectWithValue({ message: error.message || 'Network error occurred' });
    }
  }
);

// NEW: Role and Permission Management Thunks
export const fetchRoles = createAsyncThunk(
  'admin/fetchRoles',
  async (params?: RoleQueryParams) => {
    console.log('fetchRoles called with params:', params);
    return await getAllRolesAPI(params);
  }
);

export const fetchPermissions = createAsyncThunk(
  'admin/fetchPermissions',
  async (params?: PermissionQueryParams) => {
    console.log('fetchPermissions called with params:', params);
    return await getAllPermissionsAPI(params);
  }
);

export const fetchPermissionsGrouped = createAsyncThunk(
  'admin/fetchPermissionsGrouped',
  async (params?: PermissionQueryParams) => {
    console.log('fetchPermissionsGrouped called with params:', params);
    return await getPermissionsGroupedByCategoryAPI();
  }
);

export const deleteRole = createAsyncThunk(
  'admin/deleteRole',
  async (uuid: string) => {
    await deleteRoleAPI(uuid);
    return uuid;
  }
);

// Slice
const adminSlice = createSlice({
  name: 'admin',
  initialState,
  reducers: {
    setFilters: (state, action: PayloadAction<UserQueryParams>) => {
      state.filters = { ...state.filters, ...action.payload };
      // Sync pagination state with filters
      if (action.payload.page !== undefined) {
        state.pagination.currentPage = action.payload.page;
      }
      if (action.payload.limit !== undefined) {
        state.pagination.pageSize = action.payload.limit;
      }
    },
    clearFilters: (state) => {
      state.filters = {
        page: 1,
        limit: 10,
      };
      // Reset pagination state to defaults
      state.pagination.currentPage = 1;
      state.pagination.pageSize = 10;
    },
    setRolesFilters: (state, action: PayloadAction<Partial<RolesState>>) => {
      state.rolesFilters = { ...state.rolesFilters, ...action.payload };
    },
    clearRolesFilters: (state) => {
      state.rolesFilters = {
        page: 1,
        limit: 10,
        includeDeleted: false,
        includeSystemRoles: true,
      };
    },
    setCurrentUser: (state, action: PayloadAction<User | null>) => {
      state.currentUser = action.payload;
    },
    clearError: (state) => {
      state.error = null;
    },
    clearRolePermissionError: (state) => {
      state.rolePermissionError = null;
    },
  },
  extraReducers: (builder) => {
    // Fetch Users
    builder.addCase(fetchUsers.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(fetchUsers.fulfilled, (state, action) => {
      state.loading = false;
      // action.payload is now the unwrapped data with { data, pagination } structure
      if (action.payload.data && action.payload.pagination) {
        state.users = action.payload.data;
        state.pagination = action.payload.pagination;
      } else if (Array.isArray(action.payload)) {
        // Fallback if it's just an array
        state.users = action.payload;
      } else {
        state.users = [];
      }
    });
    builder.addCase(fetchUsers.rejected, (state, action) => {
      state.loading = false;
      state.error = action.error.message || 'Failed to fetch users';
    });

    // Fetch User by ID
    builder.addCase(fetchUserById.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(fetchUserById.fulfilled, (state, action) => {
      state.loading = false;
      state.currentUser = action.payload;
    });
    builder.addCase(fetchUserById.rejected, (state, action) => {
      state.loading = false;
      state.error = (action.payload as any)?.message || action.error.message || 'Failed to fetch user';
    });

    // Create User
    builder.addCase(createUser.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(createUser.fulfilled, (state, action) => {
      state.loading = false;
      state.users.unshift(action.payload);
      state.pagination.totalItems += 1;
    });
    builder.addCase(createUser.rejected, (state, action) => {
      state.loading = false;
      state.error = (action.payload as any)?.message || action.error.message || 'Failed to create user';
    });

    // Update User
    builder.addCase(updateUser.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(updateUser.fulfilled, (state, action) => {
      state.loading = false;
      const index = state.users.findIndex(user => user.uuid === action.payload.uuid);
      if (index !== -1) {
        state.users[index] = action.payload;
      }
      if (state.currentUser?.uuid === action.payload.uuid) {
        state.currentUser = action.payload;
      }
    });
    builder.addCase(updateUser.rejected, (state, action) => {
      state.loading = false;
      state.error = (action.payload as any)?.message || action.error.message || 'Failed to update user';
    });

    // Delete User
    builder.addCase(deleteUser.fulfilled, (state, action) => {
      state.users = state.users.filter(user => user.uuid !== action.payload);
      state.pagination.totalItems -= 1;
      if (state.currentUser?.uuid === action.payload) {
        state.currentUser = null;
      }
    });

    // Restore User
    builder.addCase(restoreUser.fulfilled, (state, action) => {
      // Note: In a real app, you might want to refetch the users list
      // or update the user in place if you're showing deleted users
      state.pagination.totalItems += 1;
    });

    // Update User Role
    builder.addCase(updateUserRole.fulfilled, (state, action) => {
      const index = state.users.findIndex(user => user.uuid === action.payload.uuid);
      if (index !== -1) {
        state.users[index] = action.payload;
      }
    });

    // Toggle User Status
    builder.addCase(toggleUserStatus.fulfilled, (state, action) => {
      const index = state.users.findIndex(user => user.uuid === action.payload.uuid);
      if (index !== -1) {
        state.users[index] = action.payload;
      }
    });

    // Fetch Statistics
    builder.addCase(fetchStatistics.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(fetchStatistics.fulfilled, (state, action) => {
      state.loading = false;
      state.statistics = action.payload;
      console.log('Statistics updated in store:', action.payload);
    });
    builder.addCase(fetchStatistics.rejected, (state, action) => {
      state.loading = false;
      state.error = (action.payload as any)?.message || action.error.message || 'Failed to fetch statistics';
      console.error('Statistics fetch failed:', action.error);
    });

    // NEW: Role and Permission Management Cases
    // Fetch Roles
    builder.addCase(fetchRoles.pending, (state) => {
      state.rolesLoading = true;
      state.rolePermissionError = null;
    });
    builder.addCase(fetchRoles.fulfilled, (state, action) => {
      state.rolesLoading = false;
      // Handle response from safeApi wrapper
      if (action.payload.status && action.payload.data) {
        // For now, assume it's always an array since pagination isn't implemented server-side yet
        state.roles = Array.isArray(action.payload.data) ? action.payload.data : [];
      } else {
        state.roles = [];
      }
    });
    builder.addCase(fetchRoles.rejected, (state, action) => {
      state.rolesLoading = false;
      state.rolePermissionError = action.error.message || 'Failed to fetch roles';
    });

    // Fetch Permissions
    builder.addCase(fetchPermissions.pending, (state) => {
      state.permissionsLoading = true;
      state.rolePermissionError = null;
    });
    builder.addCase(fetchPermissions.fulfilled, (state, action) => {
      state.permissionsLoading = false;
      // safeApi returns ApiResponse<T>, so we need to access action.payload.data
      state.permissions = (action.payload.status && action.payload.data) ? action.payload.data : [];
    });
    builder.addCase(fetchPermissions.rejected, (state, action) => {
      state.permissionsLoading = false;
      state.rolePermissionError = action.error.message || 'Failed to fetch permissions';
    });

    // Fetch Permissions Grouped
    builder.addCase(fetchPermissionsGrouped.pending, (state) => {
      state.permissionsLoading = true;
      state.rolePermissionError = null;
    });
    builder.addCase(fetchPermissionsGrouped.fulfilled, (state, action) => {
      state.permissionsLoading = false;
      // safeApi returns ApiResponse<T>, so we need to access action.payload.data
      state.permissionsGrouped = (action.payload.status && action.payload.data) ? action.payload.data : {};
    });
    builder.addCase(fetchPermissionsGrouped.rejected, (state, action) => {
      state.permissionsLoading = false;
      state.rolePermissionError = action.error.message || 'Failed to fetch grouped permissions';
    });

    // Delete Role
    builder.addCase(deleteRole.fulfilled, (state, action) => {
      state.roles = state.roles.filter(role => role.uuid !== action.payload);
    });
  },
});

export const { 
  setFilters, 
  clearFilters, 
  setRolesFilters, 
  clearRolesFilters, 
  setCurrentUser, 
  clearError, 
  clearRolePermissionError 
} = adminSlice.actions;
export default adminSlice.reducer;